import React, { useEffect, useMemo, useState } from "react";
import axios from "../../../../../api/axios";

const LinkEntityToProduct = ({
  isOpen,
  onClose,
  groupId,
  onProductAdded,
  entities,
}) => {

  const [activeTab, setActiveTab] = useState("product");
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [subscriptionPeriod, setSubscriptionPeriod] = useState("");
  const [paymentMode, setPaymentMode] = useState("");
  const [receivedAmount, setReceivedAmount] = useState(0);
  const [transactionRef, setTransactionRef] = useState("");
  const [nextDueDate, setNextDueDate] = useState("");

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const userData = JSON.parse(sessionStorage.getItem("userData"));

  useEffect(() => {
    if (!isOpen) return;

    const fetchProducts = async () => {
      setProductsLoading(true);
      try {
        const res = await axios.post(
          "/businessEntities/getAllProductsAndPlans",
          {},
          { headers: { "Content-Type": "application/json" } },
        );
        setProducts(res.data.products || []);
      } catch {
        setError("Failed to load products");
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
  }, [isOpen]);

  const filteredProducts = useMemo(() => {
    if (!selectedEntity) return [];

    const entity = entities.find(
      (e) => Number(e.business_entity_id) === Number(selectedEntity),
    );
    if (!entity) return [];

    return products.filter(
      (p) =>
        p.business_type === entity.business_type || p.business_type === "ALL",
    );
  }, [selectedEntity, products, entities]);

  const handleNext = () => {
    if (!selectedEntity || !selectedProduct) {
      setError("Please select restaurant and product");
      return;
    }
    setError(null);
    setActiveTab("subscription");
  };

  const handleSave = async () => {
    if (!subscriptionPeriod || !paymentMode || !receivedAmount) {
      setError("Fill all required subscription details");
      return;
    }

    if (receivedAmount < selectedProduct.yearly_price * 0.5) {
      setError("Minimum 50% payment required");
      return;
    }

    if (receivedAmount !== selectedProduct.yearly_price && !nextDueDate) {
      setError("Next due date required for partial payment");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await axios.post(
        "/businessEntities/addProductForBusiness",
        {
          businessEntityId: selectedEntity,
          productId: selectedProduct.product_id,
          groupId,
          subscriptionPeriod,
          paymentMode,
          receivedAmount,
          transactionRef,
          nextDueDate: nextDueDate || null,
          userId: userData.userId,
        },
        { headers: { "Content-Type": "application/json" } },
      );

      onProductAdded();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to add product");
    } finally {
      setIsLoading(false);
      setActiveTab("product");
    }
  };

  return (
    <div
      className="modal fade show d-block"
      style={{ background: "rgba(0,0,0,.5)" }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold">Add Product</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          {/* Tabs */}
          <div className="d-flex gap-3 border-bottom pb-2 mb-4">
            <button
              className={`btn ${activeTab === "product" ? "btn-primary" : "btn-outline-secondary"}`}
              onClick={() => setActiveTab("product")}
            >
              Product
            </button>
            <button
              className={`btn ${activeTab === "subscription" ? "btn-primary" : "btn-outline-secondary"}`}
              disabled={!selectedProduct}
              onClick={() => setActiveTab("subscription")}
            >
              Subscription Plan
            </button>
          </div>

          {/* TAB 1 */}
          {activeTab === "product" && (
            <>
              <select
                className="form-select mb-3"
                value={selectedEntity}
                onChange={(e) => {
                  setSelectedEntity(e.target.value);
                  setSelectedProduct(null);
                }}
              >
                <option value="">Select Restaurant</option>
                {entities.map((e) => (
                  <option
                    key={e.business_entity_id}
                    value={e.business_entity_id}
                  >
                    {e.business_name} | {e.business_type}
                  </option>
                ))}
              </select>

              <select
                className="form-select mb-3"
                disabled={!selectedEntity || productsLoading}
                value={selectedProduct?.product_id || ""}
                onChange={(e) => {
                  const product = filteredProducts.find(
                    (p) => p.product_id === Number(e.target.value),
                  );
                  setSelectedProduct(product);
                  setReceivedAmount(product?.yearly_price || 0);
                }}
              >
                <option value="">Select Product</option>
                {filteredProducts.map((p) => (
                  <option key={p.product_id} value={p.product_id}>
                    {p.product_name}
                  </option>
                ))}
              </select>

              <div className="text-end">
                <button className="btn btn-primary" onClick={handleNext}>
                  Next
                </button>
              </div>
            </>
          )}

          {/* TAB 2 */}
          {activeTab === "subscription" && (
            <>
              {/* same subscription UI you already built */}
              {/* keeping this short to avoid repetition */}
              <button
                className="btn btn-primary w-100"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading ? "Saving..." : "Add Product"}
              </button>

              <button
                className="btn btn-outline-secondary w-100 mt-2"
                onClick={() => setActiveTab("product")}
              >
                Back
              </button>
            </>
          )}

          {error && <div className="alert alert-danger mt-3">{error}</div>}
        </div>
      </div>
    </div>
  );
};

export default LinkEntityToProduct;
